// What an account may do is decided by the access token and nothing else.
// Login used to copy the scopes out of the token into sessionStorage and no
// code ever wrote them again, so an open session kept the permissions it was
// born with: changing an account's access changed nothing on screen, not even
// after a reload, because sessionStorage survives one. The copy is rebuilt from
// the token here instead, and a token that has run out ends the session rather
// than leaving a signed out account holding its old menu.

export const decodeToken = (token) => {
  if (!token || token === "null" || token === "undefined") return null;
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
};

// `exp` is in seconds; a token without one is treated as still valid
export const isExpired = (claims) =>
  !!claims && !!claims.exp && claims.exp * 1000 <= Date.now();

/**
 * Writes everything the screens read out of the token. Unconditional on
 * purpose: a claim that is missing has to clear the old value, otherwise the
 * previous session's permissions leak into this one.
 */
export const applyClaims = (claims) => {
  if (!claims) return false;

  sessionStorage.setItem("scopes", (claims.scopes || []).toString());
  sessionStorage.setItem(
    "allowedJobStatuses",
    (claims.allowedJobStatuses || []).toString()
  );

  if (claims.merchantId) {
    sessionStorage.setItem("merchantId", claims.merchantId);
  } else {
    sessionStorage.removeItem("merchantId");
  }

  // a garage login is pinned to one garage for the whole session
  if (claims.branchId) {
    sessionStorage.setItem("garageBranchId", claims.branchId);
  } else {
    sessionStorage.removeItem("garageBranchId");
  }

  return true;
};

export const clearSession = () => {
  sessionStorage.setItem("isloggedin", false);
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("scopes");
  sessionStorage.removeItem("allowedJobStatuses");
  sessionStorage.removeItem("merchantId");
  sessionStorage.removeItem("garageBranchId");
};

/**
 * Run on every entry into /admin. Returns false when the session is over, so
 * the caller can send the visitor back to the login screen.
 */
export const syncSessionFromToken = () => {
  const claims = decodeToken(sessionStorage.getItem("token"));

  if (!claims || isExpired(claims)) {
    clearSession();
    return false;
  }

  return applyClaims(claims);
};
